import { useEffect, useState, useRef } from "react";
import { callApi } from "../../../utils/api";
import {
    ToastsContainer,
    ToastsContainerPosition,
    ToastsStore,
} from "react-toasts";

const useHintRecorder = ({ fileId, refetchAudioList, page }) => {
    const [audioURL, setAudioURL] = useState("");
    const [isRecording, setIsRecording] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [recorder, setRecorder] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [timer, setTimer] = useState(0);
    const intervalRef = useRef(null);

    const blobToBase64 = (blob) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const uploadAudio = async (params) => {
        const user = localStorage.getItem("user");
        const user_idfk = user ? JSON.parse(user)["userId"] : "";
        if (!fileId) {
            ToastsStore.error("File ID is missing. Cannot upload audio.");
            return;
        }
        setIsUploading(true);
        try {
            const res = {
                file_id: fileId,
                user_id: user_idfk,
                audio: params.data,
                content_type: params.contentType || "audio/mpeg",
            };
            const response = await callApi("POST", "hintaudio_upload.php", res);
            ToastsStore.success("Audio saved successfully");
            // Call the callback after successful upload
            if (refetchAudioList) {
                refetchAudioList();
            }
        } catch (err) {
            console.error("Upload error:", err);
            ToastsStore.error("Failed to upload audio");
        } finally {
            setIsUploading(false);
        }
    };

    useEffect(() => {
        // Lazily obtain recorder first time we're recording
        if (recorder === null) {
            if (isRecording) {
                requestRecorder().then(setRecorder, (error) => {
                    console.error("Recorder initialization error:", error);
                    ToastsStore.error("Failed to initialize recorder");
                    setIsRecording(false);
                });
            }
            return;
        }

        // Manage recorder state
        if (isRecording) {
            recorder.start();
        } else {
            recorder.stop();
        }

        // Handle audio data when available
        const handleData = async (e) => {
            try {
                const response = await blobToBase64(e.data);
                const contentType = e.data.type || "audio/mpeg";
                await uploadAudio({
                    data: response,
                    contentType,
                });
            } catch (error) {
                console.error("Blob to base64 error:", error);
                ToastsStore.error("Failed to process audio data");
                setIsUploading(false);
            }
        };

        recorder.addEventListener("dataavailable", handleData);
        return () => recorder.removeEventListener("dataavailable", handleData);
    }, [recorder, isRecording]);

    const startRecording = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        setTimer(0);
        setIsRecording(true);
        setStartTime(performance.now());
        intervalRef.current = setInterval(() => {
            setTimer((prevTimer) => prevTimer + 1);
        }, 1000);
    };

    const stopRecording = () => {
        setIsRecording(false);
        setStartTime(null);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setTimer(0);
    };

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    return { audioURL, isRecording, isUploading, startRecording, stopRecording, startTime, timer };
};

async function requestRecorder() {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        return new MediaRecorder(stream);
    } catch (error) {
        throw new Error("Failed to access microphone: " + error.message);
    }
}

export default useHintRecorder;